import React, { useMemo, useState, useEffect } from 'react';
import { ArrowLeft } from './Icons';
import BottomNavigation from './BottomNavigation';
import { grammarTopicsData } from '../data/grammarTopicsData';
import grammarSpeakDataService from '../services/grammarSpeakDataService';
import { getSupabaseClient } from '../utils/supabaseClient';

const GrammarTopicsPage = ({ onBack, userPreferences, onNavigateToTopic }) => {
  const [selectedMode, setSelectedMode] = useState('read');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupsData, setGroupsData] = useState(null);
  const [topicsData, setTopicsData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { displayLanguage } = userPreferences;

  // Load groups and topics using A2A data service (with Supabase fallback)
  useEffect(() => {
    let isMounted = true;
    
    async function loadData() {
      setLoading(true);
      try {
        console.log('[GrammarTopicsPage] Loading grammar topics via A2A...');
        
        // Load groups and topics from A2A
        const [groups, topics] = await Promise.all([
          grammarSpeakDataService.getGrammarGroups().catch(err => {
            console.warn('[GrammarTopicsPage] Failed to load groups via A2A, will use fallback:', err);
            return null;
          }),
          grammarSpeakDataService.getGrammarTopics().catch(err => {
            console.warn('[GrammarTopicsPage] Failed to load topics via A2A, will use fallback:', err);
            return null;
          })
        ]);
        
        console.log('[GrammarTopicsPage] A2A data loaded:', {
          groupsCount: groups?.length || 0,
          topicsCount: topics?.length || 0
        });
        
        // Transform groups data
        const transformedGroups = Array.isArray(groups) ? groups.map(row => ({
          id: row.id,
          icon: row.icon,
          title: {
            tc: row.title_tc || row.title?.tc,
            sc: row.title_sc || row.title?.sc,
            en: row.title_en || row.title?.en || row.title
          },
          subtitle: {
            tc: row.subtitle_tc || row.subtitle?.tc,
            sc: row.subtitle_sc || row.subtitle?.sc,
            en: row.subtitle_en || row.subtitle?.en || row.subtitle
          },
          difficulty: row.difficulty,
          color: row.color,
          topics: []
        })) : [];
        
        // Transform topics data
        const transformedTopics = Array.isArray(topics) ? topics.map(row => ({
          id: row.id || row.topic_id,
          group_id: row.group_id || row.groupId,
          icon: row.icon,
          title: {
            tc: row.title_tc || row.title?.tc,
            sc: row.title_sc || row.title?.sc,
            en: row.title_en || row.title?.en || row.title
          },
          description: {
            tc: row.description_tc || row.description?.tc,
            sc: row.description_sc || row.description?.sc,
            en: row.description_en || row.description?.en || row.description
          },
          difficulty: row.difficulty
        })) : [];
        
        if (isMounted) {
          setTopicsData(transformedTopics);
          setGroupsData(transformedGroups);
          console.log('[GrammarTopicsPage] Data loaded successfully:', {
            topicsCount: transformedTopics.length,
            groupsCount: transformedGroups.length
          });
        }
      } catch (error) {
        console.error('[GrammarTopicsPage] Error loading data:', error);
        // Try Supabase fallback for both groups and topics
        try {
          const supabase = getSupabaseClient();
          if (supabase) {
            const [groupsResult, topicsResult] = await Promise.all([
              supabase.from('eng_grammargroups').select('*'),
              supabase.from('eng_grammartopics').select('*')
            ]);
            
            if (!groupsResult.error && groupsResult.data) {
              const transformedGroups = groupsResult.data.map(row => ({
                id: row.id,
                icon: row.icon,
                title: {
                  tc: row.title_tc,
                  sc: row.title_sc,
                  en: row.title_en
                },
                subtitle: {
                  tc: row.subtitle_tc,
                  sc: row.subtitle_sc,
                  en: row.subtitle_en
                },
                difficulty: row.difficulty,
                color: row.color,
                topics: []
              }));
              
              if (isMounted) {
                setGroupsData(transformedGroups);
                console.log('[GrammarTopicsPage] Groups loaded from Supabase fallback:', transformedGroups.length);
              }
            }
            
            if (!topicsResult.error && topicsResult.data) {
              const transformedTopics = topicsResult.data.map(row => ({
                id: row.id,
                group_id: row.group_id,
                icon: row.icon,
                title: {
                  tc: row.title_tc,
                  sc: row.title_sc,
                  en: row.title_en
                },
                description: {
                  tc: row.description_tc,
                  sc: row.description_sc,
                  en: row.description_en
                },
                difficulty: row.difficulty
              }));
              
              if (isMounted) {
                setTopicsData(transformedTopics);
                console.log('[GrammarTopicsPage] Topics loaded from Supabase fallback:', transformedTopics.length);
              }
            }
          }
        } catch (fallbackError) {
          console.error('[GrammarTopicsPage] Supabase fallback also failed:', fallbackError);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    loadData();
    return () => { isMounted = false; };
  }, []);

  const data = useMemo(() => {
    console.log('GrammarTopicsPage - groupsData:', groupsData);
    console.log('GrammarTopicsPage - topicsData:', topicsData);
    
    if (!groupsData || !topicsData || 
        (Array.isArray(groupsData) && groupsData.length === 0) ||
        (Array.isArray(topicsData) && topicsData.length === 0)) {
      console.log('GrammarTopicsPage - Using fallback data');
      return grammarTopicsData;
    }

    const groups = Array.isArray(groupsData) ? groupsData : [groupsData];
    const topics = Array.isArray(topicsData) ? topicsData : [topicsData];

    console.log('GrammarTopicsPage - Processing groups:', groups.length);
    console.log('GrammarTopicsPage - Processing topics:', topics.length);

    // Group topics by group_id
    const topicsByGroup = topics.reduce((acc, topic) => {
      const groupId = topic.group_id || topic.groupId;
      if (!groupId) {
        console.warn('[GrammarTopicsPage] Topic missing group_id:', topic);
        return acc;
      }
      if (!acc[groupId]) {
        acc[groupId] = [];
      }
      acc[groupId].push(topic);
      return acc;
    }, {});

    console.log('[GrammarTopicsPage] Topics grouped by group_id:', Object.keys(topicsByGroup).map(id => ({
      groupId: id,
      topicCount: topicsByGroup[id].length
    })));

    // Add topics to their respective groups
    const groupsWithTopics = groups.map(group => {
      const groupTopics = topicsByGroup[group.id] || [];
      console.log(`[GrammarTopicsPage] Group ${group.id} has ${groupTopics.length} topics`);
      return {
        ...group,
        topics: groupTopics
      };
    });

    console.log('GrammarTopicsPage - Final groups with topics:', groupsWithTopics.map(g => ({
      id: g.id,
      title: g.title,
      topicCount: g.topics.length
    })));

    return {
      overview: grammarTopicsData.overview,
      learningModes: grammarTopicsData.learningModes,
      grammarGroups: groupsWithTopics,
      userProgress: grammarTopicsData.userProgress,
      navigation: grammarTopicsData.navigation,
      statusMessages: grammarTopicsData.statusMessages
    };
  }, [groupsData, topicsData]);

  const getText = (textObj) => {
    if (!textObj) return '';
    const langKey = displayLanguage === 'traditional'
      ? 'tc'
      : displayLanguage === 'simplified'
      ? 'sc'
      : displayLanguage === 'english'
      ? 'en'
      : displayLanguage;
    return textObj[langKey] || textObj.en || textObj.tc || textObj.sc || '';
  };

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };

  const handleTopicSelect = (group, topic, event) => {
    if (event) {
      event.stopPropagation(); // Prevent event from bubbling to parent group
    }
    console.log('[GrammarTopicsPage] Topic selected:', {
      groupId: group.id,
      topicId: topic.id,
      mode: selectedMode,
      hasCallback: !!onNavigateToTopic
    });
    if (onNavigateToTopic) {
      onNavigateToTopic(group.id, topic.id, selectedMode);
    } else {
      console.warn('[GrammarTopicsPage] onNavigateToTopic callback not provided');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500';
      case 'intermediate':
        return 'bg-yellow-500';
      case 'advanced':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getDifficultyText = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return { tc: '初級', sc: '初级', en: 'BEGINNER' };
      case 'intermediate':
        return { tc: '中級', sc: '中级', en: 'INTERMEDIATE' };
      case 'advanced':
        return { tc: '高級', sc: '高级', en: 'ADVANCED' };
      default:
        return { tc: '未知', sc: '未知', en: 'UNKNOWN' };
    }
  };

  const getProgressStatus = (group, topic) => {
    // Mock progress - would be replaced with actual user progress
    const progress = data.userProgress;
    if (progress.completedTopics.includes(topic.id)) {
      return 'completed';
    }
    return 'notStarted';
  };

  const getProgressIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'inProgress':
        return '🔄';
      case 'notStarted':
        return '⭕';
      default:
        return '⭕';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 pt-12 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">
            {getText(data.overview.title)}
          </h1>
          <div className="w-6"></div> {/* Spacer for centering */}
        </div>
        
        {/* Subtitle */}
        <p className="text-center text-gray-300 mb-6">
          {getText(data.overview.subtitle)}
        </p>
      </div>

      {/* Learning Modes Section */}
      <div className="px-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {getText({ tc: '學習模式', sc: '学习模式', en: 'Learning Modes' })}
        </h2>
        
        <div className="grid grid-cols-1 gap-4">
          {Object.entries(data.learningModes).map(([key, mode]) => (
            <div
              key={key}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedMode === key
                  ? 'border-blue-400 bg-blue-900/50'
                  : 'border-gray-600 bg-gray-800/50 hover:border-gray-500'
              }`}
              onClick={() => handleModeSelect(key)}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{mode.icon}</span>
                <div className="flex-1">
                  <h3 className="font-semibold">{getText(mode.title)}</h3>
                  <p className="text-sm text-gray-300">{getText(mode.description)}</p>
                </div>
                {selectedMode === key && (
                  <span className="text-blue-400">✓</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grammar Groups Section */}
      {selectedMode && (
        <div className="px-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">
            {getText({ tc: '語法主題', sc: '语法主题', en: 'Grammar Topics' })}
          </h2>
          
          <div className="space-y-4">
            {data.grammarGroups.map((group) => (
              <div
                key={group.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedGroup?.id === group.id
                    ? 'border-blue-400 bg-blue-900/50'
                    : 'border-gray-600 bg-gray-800/50 hover:border-gray-500'
                }`}
                onClick={() => handleGroupSelect(group)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{group.icon}</span>
                    <div>
                      <h3 className="font-semibold">{getText(group.title)}</h3>
                      <p className="text-sm text-gray-300">{getText(group.subtitle)}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(group.difficulty)}`}>
                    {getText(getDifficultyText(group.difficulty))}
                  </span>
                </div>

                {/* Topics within the group */}
                {selectedGroup?.id === group.id && (
                  <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                    {group.topics && group.topics.length > 0 ? (
                      group.topics.map((topic) => {
                        const progressStatus = getProgressStatus(group, topic);
                        return (
                          <div
                            key={topic.id}
                            className="p-3 rounded-lg bg-gray-700/50 border border-gray-600 cursor-pointer hover:bg-gray-700/70 transition-all"
                            onClick={(e) => handleTopicSelect(group, topic, e)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg">{topic.icon}</span>
                                <div>
                                  <h4 className="font-medium">{getText(topic.title)}</h4>
                                  <p className="text-sm text-gray-300">{getText(topic.description)}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">{getProgressIcon(progressStatus)}</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(topic.difficulty)}`}>
                                  {getText(getDifficultyText(topic.difficulty))}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-gray-400 text-sm">
                        {getText({ tc: '此分類暫無主題', sc: '此分类暂无主题', en: 'No topics in this category' })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Summary */}
      <div className="px-6 mb-8">
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-600">
          <h3 className="font-semibold mb-3">
            {getText({ tc: '學習進度', sc: '学习进度', en: 'Learning Progress' })}
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-400">2</div>
              <div className="text-sm text-gray-300">
                {getText({ tc: '已完成', sc: '已完成', en: 'Completed' })}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400">5</div>
              <div className="text-sm text-gray-300">
                {getText({ tc: '進行中', sc: '进行中', en: 'In Progress' })}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-400">83</div>
              <div className="text-sm text-gray-300">
                {getText({ tc: '待學習', sc: '待学习', en: 'To Learn' })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentView="grammar_topics"
        onHomeClick={onBack}
        onLearningClick={onBack}
        onChallengeClick={onBack}
        onSocialClick={onBack}
      />
    </div>
  );
};

export default GrammarTopicsPage;
